// Логика страницы заказчика (ТЗ 6.6, 6.10): персональная ссылка из адреса, чтение спецификации в
// срезе заказчика, утверждение и отклонение, публичная загрузка файла, чаты и сообщения.
// Общая для двух страниц заказчика — таблицы и чатов, — поэтому лежит в модели, а не в компоненте.
// Сгенерировано Claude.
import api from '@/api/specCustomer';
import errorModel from '@/model/error';
import { CATEGORIES, categoryTitle } from '@/utils/spec';

/** Сообщение по неверной ссылке и по исчерпанному ограничителю частоты (ТЗ 3.3, 6.6, 6.9). */
const LINK_ERRORS = {
  400: 'Ссылка недействительна. Запросите у дизайнера новую ссылку на спецификацию.',
  403: 'Спецификация недоступна по этой ссылке.',
  404: 'Ссылка недействительна. Запросите у дизайнера новую ссылку на спецификацию.',
  429: 'Слишком много запросов по этой ссылке. Повторите попытку позже.',
};

const LINK_ERROR_DEFAULT = 'Не удалось открыть спецификацию по ссылке.';
const CHATS_ERROR_DEFAULT = 'Не удалось открыть чаты по этой ссылке.';

export default {
  /** Внешний идентификатор и подпись — из адреса страницы (ТЗ 6.1, 3.3). */
  linkOf(route) {
    return { extId: route.params.extId, token: route.params.token };
  },

  /**
   * Сообщение по коду отказа публичной операции: неверная подпись и несуществующая ссылка (400,
   * 404), спецификация-черновик или архив без доступа (403), исчерпанный ограничитель частоты
   * (429) — ТЗ 3.3, 6.6, 6.9. Одно правило на обе страницы заказчика.
   */
  linkErrorText(error, fallback = LINK_ERROR_DEFAULT) {
    return LINK_ERRORS[error?.status] ?? fallback;
  },

  /**
   * Спецификация в срезе заказчика. Отказ не бросается наружу: страница показывает сообщение
   * (ТЗ 6.6), поэтому результат — либо `{ view }`, либо `{ error }` с готовым текстом.
   */
  async fetchSpecification({ extId, token }, withPreloader = true) {
    try {
      return { view: await api.fetchSpecification(extId, token, withPreloader) };
    } catch (e) {
      // отказ уже показан сообщением на странице — общий разбор ошибок гасим, чтобы
      // вместо таблицы не подменялся весь экран (ТЗ 6.9)
      errorModel.clear();
      return { error: this.linkErrorText(e) };
    }
  },

  approve({ extId, token }, itemIds) {
    return api.approve(extId, token, itemIds, true);
  },

  reject({ extId, token }, payload) {
    return api.reject(extId, token, payload, true);
  },

  uploadFile({ extId, token }, file) {
    return api.uploadFile(extId, token, file, true);
  },

  /**
   * Чаты заказчика. Как и у таблицы, отказ не бросается наружу: страница показывает сообщение,
   * выбранное по коду ответа (ТЗ 6.9), поэтому результат — `{ items }` либо `{ error }`.
   */
  async fetchChats({ extId, token }, withPreloader = true) {
    try {
      const result = await api.fetchChats(extId, token, withPreloader);
      return { items: result.items ?? [] };
    } catch (e) {
      // сообщение показывает сама страница — общий разбор ошибок гасим (ТЗ 6.9)
      errorModel.clear();
      return { error: this.linkErrorText(e, CHATS_ERROR_DEFAULT) };
    }
  },

  fetchMessages({ extId, token }, chatId, withPreloader = true) {
    return api.fetchMessages(extId, token, chatId, withPreloader).then((r) => r.items ?? []);
  },

  /**
   * Сообщение заказчика: вложения загружаются публичной операцией по той же ссылке (ТЗ 3.3, 4.3),
   * последовательно — чтобы порядок вложений в сообщении сохранился.
   */
  async sendMessage(link, chatId, text, files = []) {
    const ids = await this.uploadFiles(link, files);
    const payload = { text: text || null, files: ids };
    return api.sendMessage(link.extId, link.token, chatId, payload, true);
  },

  uploadFiles(link, files) {
    return Array.from(files ?? []).reduce(
      (chain, file) => chain.then((acc) => this.uploadFile(link, file)
        .then((result) => [...acc, result.id])),
      Promise.resolve([]),
    );
  },

  /**
   * Группы таблицы заказчика — категории в порядке спецификации (ТЗ 6.6, 2.2): показываются только
   * непустые, как у дизайнера. Порядок задаёт `categoryOrder` из ответа.
   */
  groups(view, items) {
    const order = view?.categoryOrder?.length ? view.categoryOrder : Object.keys(CATEGORIES);
    const known = order.filter((code) => Object.keys(CATEGORIES).includes(code));
    const rest = Object.keys(CATEGORIES).filter((code) => !known.includes(code));

    return [...known, ...rest]
      .map((code) => ({
        key: code,
        title: categoryTitle(code),
        items: (items ?? []).filter((item) => item.category === code),
      }))
      .filter((group) => group.items.length > 0);
  },
};
